// Horizontal clearances between actual outlines, never between object centres.
const cross = (a, b) => a.x * b.y - a.y * b.x;
const minus = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const edges = ring => ring.map((p, i) => [p, ring[(i + 1) % ring.length]]);
const bounds = ring => ({ minX: Math.min(...ring.map(p => p.x)), maxX: Math.max(...ring.map(p => p.x)),
  minY: Math.min(...ring.map(p => p.y)), maxY: Math.max(...ring.map(p => p.y)) });
const boxDistance = (a, b) => Math.hypot(Math.max(0, a.minX - b.maxX, b.minX - a.maxX),
  Math.max(0, a.minY - b.maxY, b.minY - a.maxY));
const nearest = (p, a, b) => {
  const v = minus(b, a), size = v.x * v.x + v.y * v.y;
  const t = size ? Math.max(0, Math.min(1, ((p.x - a.x) * v.x + (p.y - a.y) * v.y) / size)) : 0;
  return { x: a.x + v.x * t, y: a.y + v.y * t };
};
const inside = (p, ring) => {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < (b.x - a.x) * (p.y - a.y) / (b.y - a.y) + a.x) hit = !hit;
  }
  return hit;
};

export function outlineClearance(first, second, boundaryOnly = false) {
  if (!first?.length || !(second?.length >= 2)) return null;
  let best = null;
  const consider = (a, b) => {
    const d = distance(a, b);
    if (!best || d < best.distance) best = { a, b, distance: d };
  };
  if (!boundaryOnly) {
    const contained = first.find(p => inside(p, second)) || second.find(p => inside(p, first));
    if (contained) return { a: contained, b: contained, distance: 0 };
  }
  for (const [a, b] of edges(first)) for (const [c, d] of (second.length === 2 ? [second] : edges(second))) {
    const u = minus(b, a), v = minus(d, c), w = minus(c, a), det = cross(u, v);
    if (Math.abs(det) > 1e-12) {
      const t = cross(w, v) / det, s = cross(w, u) / det;
      if (t >= 0 && t <= 1 && s >= 0 && s <= 1) {
        const p = { x: a.x + t * u.x, y: a.y + t * u.y };
        return { a: p, b: p, distance: 0 };
      }
    }
    consider(a, nearest(a, c, d)); consider(b, nearest(b, c, d));
    consider(nearest(c, a, b), c); consider(nearest(d, a, b), d);
  }
  return best;
}

export const moduleOutline = q => [q.low[0], q.low[1], q.high[1], q.high[0]];

/** At most five nearby guides; keep obstacles and roof edges visible beside panel gaps. */
export function moduleClearanceGuides({ selected, modules = [], obstacles = [], roof = [], maxDistance = 5 }) {
  if (!selected.length) return [];
  const sources = selected.map(q => ({ q, ring: moduleOutline(q), box: bounds(moduleOutline(q)) }));
  const selectedIds = new Set(selected.map(q => q.id));
  const targets = [
    ...modules.filter(q => !selectedIds.has(q.id)).map(q => ({ id: q.id, kind: 'module', ring: moduleOutline(q) })),
    ...obstacles.map(o => ({ ...o, kind: 'obstacle' })),
    ...edges(roof).map((ring, i) => ({ id: `roof-edge:${i}`, kind: 'roof', ring })),
  ];
  const guides = targets.flatMap(target => {
    let best = null, source;
    const targetBox = bounds(target.ring);
    for (const { q, ring, box } of sources) {
      if (boxDistance(box, targetBox) > maxDistance) continue;
      const result = outlineClearance(ring, target.ring, target.kind === 'roof');
      if (result && (!best || result.distance < best.distance)) { best = result; source = q; }
    }
    if (!best || best.distance > maxDistance) return [];
    // Draw at the moving panel's plane, even when the target is a tall obstacle.
    const [p, u, , v] = moduleOutline(source);
    const a = minus(u, p), b = minus(v, p), det = cross(a, b);
    const z = point => {
      const d = minus(point, p);
      return (p.z || 0) + (Math.abs(det) > 1e-10
        ? cross(d, b) / det * (u.z - p.z) + cross(a, d) / det * (v.z - p.z) : 0);
    };
    return [{ ...best, id: target.id, kind: target.kind, label: target.label,
      a: { ...best.a, z: z(best.a) }, b: { ...best.b, z: z(best.b) } }];
  }).sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id));
  const limits = { module: 2, obstacle: 2, roof: 1 }, counts = {};
  return guides.filter(g => { counts[g.kind] = (counts[g.kind] || 0) + 1; return counts[g.kind] <= limits[g.kind]; });
}
